import React from 'react';
import PropTypes from 'prop-types';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { Formik } from 'formik';
import * as Yup from 'yup';

import Button from '~/components/core/Atomic/Buttons/Button';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import List from '~/components/core/Atomic/List';
import ListItem from '~/components/core/Atomic/ListItem';
import ListItemText from '~/components/core/Atomic/ListItemText';
import Typography from '~/components/core/Atomic/Typography';
import CancelButton from '~/components/core/Buttons/CancelButton';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import JsonEditor from '~/components/core/Molecules/JsonEditor';
import { AddIcon } from '~/components/deprecatedMuiIcons';
import { getPopoverTriggerAria } from '~/components/ui/atoms/Popover/usePopoverState';

import { isUserFiveSigma, isUserSuperUser } from '../../../UserUtils';
import { reportAxiosError } from '../../../Utils';
import CardDialog from '../../CardDialog';
import WithConfirm from '../../ConfirmModal';
import { OrganizationLogoUploadContainer } from '../../core';
import { useCms } from '../../hooks/useCms';
import { ShowOnlyTextField, TextFieldFormik } from '../../TextFieldFormik';

import { useSysconfig } from './../SystemConfigurationScreen';
import EmailDomainsContainer from './EmailDomainsContainer';
import HolidaySubdivisionSetting from './HolidaySubdivisionSetting';
import LocaleSetting from './LocaleSetting';
import OrganizationAdjusterSelect from './OrganizationAdjusterSelect';

import { useStyles } from '../../../assets/styles';

function TscSpecificSettings(props) {
  const { organization, onUpdateSettings } = props;
  const { classes } = useStyles();
  const [showAddIp, setShowAddIp] = React.useState(false);

  const staticIpList = organization.extra_settings?.static_ip_list || [];

  return (
    <div className="flex flex-col gap-12">
      <div className={classes.container} style={{ alignItems: 'center' }}>
        <Typography>Static IPs:</Typography>
        <IconButton style={{ padding: '0px 0px 0px 16px' }} onClick={() => setShowAddIp(true)}>
          <AddIcon />
        </IconButton>
      </div>

      <List dense>
        {staticIpList.map((ip) => (
          <ListItem key={ip} dense>
            <ListItemText primary={`${ip}`} />
            <WithConfirm title={`Delete ${ip}?`} primaryButtonName="Delete" shouldCloseOnPrimary={true}>
              <IconButton
                style={{ padding: '0px 0px 0px 16px' }}
                onClick={async () => {
                  try {
                    await onUpdateSettings({ static_ip_list: staticIpList.filter((currIp) => currIp !== ip) });
                  } catch {
                    // onUpdateSettings reports the failure and this control has no editor state to
                    // unwind; the catch is what keeps the rejection from going unhandled.
                  }
                }}
              >
                <DeleteIcon />
              </IconButton>
            </WithConfirm>
          </ListItem>
        ))}
      </List>

      {showAddIp && (
        <Formik
          initialValues={{
            ip: '',
          }}
          validationSchema={Yup.object().shape({
            ip: Yup.string().test('is-valid', 'Not a valid IP address', (value) => {
              if (!value) {
                return false;
              }
              const splitIp = value.split('.');
              if (splitIp.length !== 4) {
                return false;
              }
              return splitIp.every((item) => item >= 0 && item <= 255);
            }),
          })}
          onSubmit={async (values, formikProps) => {
            try {
              await onUpdateSettings({ static_ip_list: [...staticIpList, values['ip'].trim()] });
              setShowAddIp(false);
            } catch {
              formikProps.setSubmitting(false);
            }
          }}
        >
          {(formikProps) => (
            <CardDialog isDialog title="Add static IP" onClose={() => setShowAddIp(false)}>
              <TextFieldFormik id="ip" label="IP" fullWidth />
              <div className={classes.buttonsContainer}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={formikProps.isSubmitting}
                  onClick={formikProps.handleSubmit}
                >
                  Save
                </Button>
              </div>
            </CardDialog>
          )}
        </Formik>
      )}
    </div>
  );
}

TscSpecificSettings.propTypes = {
  organization: PropTypes.object.isRequired,
  onUpdateSettings: PropTypes.func.isRequired,
};

function CustomerIdentifierSetting({ organization, onUpdateSettings }) {
  const [popoverAnchorEl, setPopoverAnchorEl] = React.useState(undefined);
  const popoverId = React.useId();
  const { classes } = useStyles();

  return (
    <>
      <ShowOnlyTextField
        classes={classes}
        label="Customer Identifier"
        showOnlyValueComponent={organization.customer_identifier || '—'}
        onEdit={(e) => setPopoverAnchorEl(e.currentTarget)}
        editTriggerAria={getPopoverTriggerAria(Boolean(popoverAnchorEl), popoverId)}
      />

      <CardDialog
        isPopover
        open={Boolean(popoverAnchorEl)}
        anchorEl={popoverAnchorEl}
        id={popoverId}
        title="Customer Identifier"
        onClose={() => setPopoverAnchorEl(undefined)}
      >
        <Formik
          initialValues={{ customer_identifier: organization.customer_identifier || '' }}
          enableReinitialize
          onSubmit={async (values, formikProps) => {
            try {
              await onUpdateSettings({ customer_identifier: values.customer_identifier.trim() || null });
              setPopoverAnchorEl(undefined);
            } catch {
              formikProps.setSubmitting(false);
            }
          }}
        >
          {(formikProps) => (
            <>
              <TextFieldFormik
                id="customer_identifier"
                label="Customer Identifier"
                className={classes.textField}
                helperText="Groups this customer's orgs across environments (Five Sigma only)."
                inputProps={{ maxLength: 256 }}
                fullWidth
              />
              <div className={classes.buttonsContainer}>
                <CancelButton
                  disabled={formikProps.isSubmitting}
                  onClick={() => {
                    formikProps.handleReset();
                    setPopoverAnchorEl(undefined);
                  }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  disabled={formikProps.isSubmitting}
                  onClick={formikProps.handleSubmit}
                >
                  Save
                </Button>
              </div>
            </>
          )}
        </Formik>
      </CardDialog>
    </>
  );
}

CustomerIdentifierSetting.propTypes = {
  organization: PropTypes.object.isRequired,
  onUpdateSettings: PropTypes.func.isRequired,
};

function OrganizationSettings({ withoutTitle, withoutSupervisor }) {
  const { organization, reloadOrganization } = useSysconfig();
  const { user } = useCms();

  const handleUpdateSettings = async (values) => {
    try {
      await axios.put(`/sysconfig/api/v1/organizations/${organization.id}`, values);
      reloadOrganization();
    } catch (error) {
      reportAxiosError(error);
      // Rethrow, as handleUpdateConfiguration below already does: every editor that uses this
      // closes itself once the promise resolves, so swallowing here makes a rejected PUT look
      // exactly like a stored one -- the popover closes, isSubmitting stays true and the screen
      // keeps showing a value the server refused (it rejects an out-of-territory subdivision and
      // an unsupported locale). Each caller handles the rejection; the two controls with nothing
      // to unwind acknowledge it so it is never an unhandled rejection.
      throw error;
    }
  };

  let SettingComponent = undefined;
  switch (organization.name.toLowerCase()) {
    case 'tsc':
      SettingComponent = TscSpecificSettings;
      break;
    default:
      SettingComponent = undefined;
  }

  const updateOrganizationLogo = async (base64Image, type) => {
    try {
      await axios.put(`/sysconfig/api/v1/organizations/${organization.id}/logo`, { base64Image, type });
      await reloadOrganization();
    } catch (error) {
      await reportAxiosError(error);
    }
  };

  const removeOrganizationLogo = async () => {
    try {
      await axios.delete(`/sysconfig/api/v1/organizations/${organization.id}/logo`);
      await reloadOrganization();
    } catch (error) {
      await reportAxiosError(error);
      throw error;
    }
  };

  return (
    <CardDialog title={withoutTitle ? undefined : 'Settings'} noCardTitle={withoutTitle}>
      <div className="flex w-full flex-col gap-20">
        {isUserFiveSigma(user) && (
          <OrganizationLogoUploadContainer onSave={updateOrganizationLogo} onRemove={removeOrganizationLogo} />
        )}

        {isUserFiveSigma(user) && (
          <CustomerIdentifierSetting organization={organization} onUpdateSettings={handleUpdateSettings} />
        )}

        {isUserFiveSigma(user) && <LocaleSetting organization={organization} onUpdateSettings={handleUpdateSettings} />}

        {isUserFiveSigma(user) && (
          <HolidaySubdivisionSetting organization={organization} onUpdateSettings={handleUpdateSettings} />
        )}

        {!withoutSupervisor && (
          <OrganizationAdjusterSelect
            id="supervisor"
            label="Supervisor"
            adjuster={organization.supervisor}
            organization={organization}
            onSelectAdjuster={(adjusterId) => handleUpdateSettings({ supervisor_id: adjusterId })}
          />
        )}

        <EmailDomainsContainer organization={organization} />

        {SettingComponent && <SettingComponent organization={organization} onUpdateSettings={handleUpdateSettings} />}

        {isUserSuperUser(user) && (
          <div className="flex">
            <OrganizationConfigurationEditor organization={organization} reloadOrganization={reloadOrganization} />
          </div>
        )}
      </div>
    </CardDialog>
  );
}

OrganizationSettings.propTypes = {
  withoutTitle: PropTypes.bool,
  withoutSupervisor: PropTypes.bool,
};

function OrganizationConfigurationEditor(props) {
  const [isConfigurationDialogOpen, setIsConfigurationDialogOpen] = React.useState(false);
  const { organization, reloadOrganization } = props;
  const { reloadOperationalDetails } = useSysconfig();

  const handleUpdateConfiguration = async (values) => {
    try {
      await axios.put(`/sysconfig/api/v1/organizations/${organization.id}`, values);
      setIsConfigurationDialogOpen(false);
      reloadOrganization();
    } catch (error) {
      reportAxiosError(error);
      throw error;
    }
  };

  const FIELD_NAME = 'configuration';

  return (
    <>
      <Button variant="contained" color="primary" onClick={() => setIsConfigurationDialogOpen(true)}>
        Edit Configuration
      </Button>
      {isConfigurationDialogOpen && (
        <Formik
          initialValues={{ [FIELD_NAME]: organization.configuration }}
          enableReinitialize
          onSubmit={(values, formikProps) => {
            handleUpdateConfiguration(values).catch(() => {
              formikProps.setSubmitting(false);
            });
          }}
        >
          {(formikProps) => {
            const { isSubmitting, handleSubmit, values, setFieldValue, setFieldError, isValid } = formikProps;

            return (
              <CardDialog
                isDialog
                maxWidth="xl"
                open={Boolean(isConfigurationDialogOpen)}
                title="Edit Configuration"
                onClose={() => setIsConfigurationDialogOpen(false)}
                footerActions={
                  <DialogFooterActions
                    disabled={isSubmitting || !isValid}
                    onClickPrimary={handleSubmit}
                    onClickSecondary={() => setIsConfigurationDialogOpen()}
                  />
                }
              >
                <JsonEditor
                  defaultValue={values?.[FIELD_NAME] || {}}
                  onChange={(value) => {
                    setFieldValue(FIELD_NAME, value);
                    reloadOperationalDetails();
                  }}
                  mode="text"
                  allowedModes={['text', 'tree']}
                  onValidationError={(errors) => {
                    if (errors?.length) {
                      setFieldError(FIELD_NAME, true);
                    }
                  }}
                  htmlElementProps={{
                    style: { height: '70vh', width: '70vw' },
                  }}
                />
              </CardDialog>
            );
          }}
        </Formik>
      )}
    </>
  );
}

OrganizationConfigurationEditor.propTypes = {
  organization: PropTypes.object.isRequired,
  reloadOrganization: PropTypes.func.isRequired,
};

export default OrganizationSettings;
